import React, { useMemo, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../apiAdmin";

interface User {
  _id: string;
  name: string;
  email: string;
  avatarUrl?: string;
}

interface Order {
  _id: string;
  dj: string | { _id: string; name: string; avatarUrl?: string };
  searchQuery: string;
  amount: number;
  createdAt: string;
  status?: "pending" | "queued" | "accepted" | "declined" | "played";
  currency?: string;
  totalAmount?: number;
  feeAmount?: number;
  paid?: boolean;
  trackArtists?: string[];
}

interface Props {
  users: User[];
  orders: Order[];
  adminsCount: number;
  setTab: (tab: "orders" | "users" | "overview" | "admins" | "revenue" | "allPayouts") => void;
}

function fmtImg(url?: string): string | null {
  if (!url) return null;
  return url.startsWith("http") ? url : `https://api.requesta.app${url}`;
}

interface OverviewPayout {
  _id: string;
  batchId: string;
  createdAt: string;
  dj: { _id: string; name: string; avatarUrl?: string };
  totalGross: number;
  totalNet: number;
  ordersCount: number;
  ordersAccepted: number;
  ordersDeclined: number;
  avgRequestValue: number;
}

export default function AdminOverviewTab({ users, orders, adminsCount, setTab }: Props) {
  const navigate = useNavigate();
  const [recentPayouts, setRecentPayouts] = useState<OverviewPayout[]>([]);

  useEffect(() => {
    adminApi.get("/payouts").then((r) => setRecentPayouts(r.data.slice(0, 10))).catch(() => {});
  }, []);

  // Stable user lookup map
  const userMap = useMemo(() => {
    const m = new Map<string, User>();
    for (const u of users) m.set(u._id, u);
    return m;
  }, [users]);

  // Stable resolveDj — depends only on userMap
  const resolveDj = useCallback(
    (djData: Order["dj"]) => {
      if (typeof djData === "object" && djData !== null) {
        return { name: djData.name, avatar: djData.avatarUrl, id: djData._id };
      }
      const found = userMap.get(djData);
      return {
        name: found ? found.name : "Unknown",
        avatar: found?.avatarUrl,
        id: djData as string,
      };
    },
    [userMap]
  );

  // Platform-wide KPI stats (all time, paid orders)
  const platformStats = useMemo(() => {
    const paid = orders.filter((o) => o.paid);
    const platformEarnings = paid.reduce((acc, o) => {
      if (typeof o.feeAmount === "number" && o.feeAmount > 0) return acc + o.feeAmount;
      if (typeof o.totalAmount === "number" && o.totalAmount > o.amount) return acc + (o.totalAmount - o.amount);
      return acc + (o.amount * 0.15) / 0.85;
    }, 0);
    const totalVolume = paid.reduce((acc, o) => {
      if (typeof o.totalAmount === "number" && o.totalAmount > 0) return acc + o.totalAmount;
      return acc + o.amount / 0.85;
    }, 0);
    const djPayouts = paid.reduce((acc, o) => acc + (o.amount || 0), 0);
    return { platformEarnings, totalVolume, djPayouts, paidCount: paid.length };
  }, [orders]);

  // Lifetime earnings per DJ id
  const djLifetimeEarnings = useMemo(() => {
    const map: Record<string, Record<string, number>> = {};
    for (const o of orders) {
      if (!o.paid) continue;
      const { id } = resolveDj(o.dj);
      const cur = (o.currency || "usd").toLowerCase();
      if (!map[id]) map[id] = {};
      map[id][cur] = (map[id][cur] || 0) + (o.amount || 0);
    }
    return map;
  }, [orders, resolveDj]);

  // Last-7-days aggregations
  const { orders7d, revenue7d, topDjs, statusMix } = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 3600 * 1000;
    const recent = orders
      .filter((o) => new Date(o.createdAt).getTime() >= cutoff)
      .slice()
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    const revMap: Record<string, number> = {};
    const djMap: Record<string, { name: string; avatar?: string; amount: number; count: number; id: string }> = {};
    const stMix: Record<string, number> = { pending: 0, queued: 0, accepted: 0, declined: 0, played: 0 };

    for (const o of recent) {
      const cur = (o.currency || "usd").toLowerCase();
      revMap[cur] = (revMap[cur] || 0) + (o.amount || 0);

      const { name, avatar, id } = resolveDj(o.dj);
      if (!djMap[id]) djMap[id] = { name, avatar, amount: 0, count: 0, id };
      djMap[id].amount += o.amount || 0;
      djMap[id].count += 1;

      if (o.status && o.status in stMix) stMix[o.status]++;
    }

    const sorted = Object.values(djMap).sort((a, b) => b.amount - a.amount).slice(0, 5);
    return { orders7d: recent, revenue7d: revMap, topDjs: sorted, statusMix: stMix };
  }, [orders, resolveDj]);

  const maxDjRev = topDjs[0]?.amount || 1;

  const fmtEarnings = (map?: Record<string, number>) => {
    if (!map || !Object.keys(map).length) return "—";
    return Object.entries(map)
      .map(([cur, amt]) => `${cur.toUpperCase()} ${amt.toFixed(0)}`)
      .join(" / ");
  };

  return (
    <div className="adm-tab-container fade-in">
      {/* KPI row */}
      <section className="adm-grid-5">
        <div className="kpi-card kpi-card-highlight" onClick={() => setTab("revenue")}>
          <div className="kpi-icon green-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#10b981" strokeWidth="2">
              <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Platform Earnings</div>
            <div className="kpi-value" style={{ color: "var(--adm-green)" }}>
              ${platformStats.platformEarnings.toFixed(2)}
            </div>
            <div className="kpi-sublabel">{platformStats.paidCount} paid orders</div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("revenue")}>
          <div className="kpi-icon blue-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
              <path d="M18 20V10M12 20V4M6 20v-6" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Total Volume</div>
            <div className="kpi-value">${platformStats.totalVolume.toFixed(2)}</div>
            <div className="kpi-sublabel">All time</div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("revenue")}>
          <div className="kpi-icon purple-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Talent Payouts</div>
            <div className="kpi-value">${platformStats.djPayouts.toFixed(2)}</div>
            <div className="kpi-sublabel">85% share</div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("orders")}>
          <div className="kpi-icon orange-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Orders (7d)</div>
            <div className="kpi-value">{orders7d.length}</div>
            <div className="kpi-sublabel">
              {Object.entries(revenue7d).map(([cur, amt]) => (
                <span key={cur}>
                  {cur.toUpperCase()} {amt.toFixed(0)}{" "}
                </span>
              ))}
            </div>
          </div>
        </div>

        <div className="kpi-card" onClick={() => setTab("users")}>
          <div className="kpi-icon blue-bg">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" strokeWidth="2">
              <path d="M17 21v-2a4 4 0 00-4-4H5a4 4 0 00-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <path d="M23 21v-2a4 4 0 00-3-3.87M16 3.13a4 4 0 010 7.75" />
            </svg>
          </div>
          <div>
            <div className="kpi-label">Total Talent</div>
            <div className="kpi-value">{users.length}</div>
            <div className="kpi-sublabel">{adminsCount} admins</div>
          </div>
        </div>
      </section>

      {/* Insights + Statuses */}
      <section className="adm-overview-grid">
        <div className="adm-card overview-insights">
          <div className="card-header-flex" style={{ marginBottom: 12 }}>
            <h2 className="adm-section-title">Top Talent (7d Revenue)</h2>
          </div>
          <div className="top-djs-list">
            {topDjs.map((dj, idx) => {
              const pct = (dj.amount / maxDjRev) * 100;
              const src = fmtImg(dj.avatar);
              return (
                <div
                  key={dj.id}
                  className="top-dj-row"
                  onClick={() => navigate(`/admin/dj/${dj.id}`)}
                  title={`Open ${dj.name}'s profile`}
                >
                  <div className="dj-info-side">
                    <span className="rank-num">#{idx + 1}</span>
                    {src ? (
                      <img src={src} className="tiny-avatar" alt="" />
                    ) : (
                      <div className="tiny-placeholder">{dj.name[0].toUpperCase()}</div>
                    )}
                    <span className="dj-name-sm dj-link-text">{dj.name}</span>
                  </div>
                  <div className="bar-container">
                    <div className="bar-fill" style={{ width: `${pct}%` }} />
                  </div>
                  <div className="dj-stat-side">
                    <span className="money-val">${dj.amount.toFixed(0)}</span>
                    <span className="count-val">{dj.count} orders</span>
                  </div>
                </div>
              );
            })}
            {topDjs.length === 0 && (
              <p className="muted-text" style={{ textAlign: "center", padding: 20 }}>
                No orders this week
              </p>
            )}
          </div>
        </div>

        <div className="adm-card overview-quick">
          <h2 className="adm-section-title" style={{ marginBottom: 14 }}>
            Order Statuses
          </h2>
          <div className="status-grid">
            {Object.entries(statusMix).map(([status, count]) => (
              <div key={status} className="status-item">
                <span className={`dot dot-${status}`} />
                <span className="status-name">{status}</span>
                <span className="status-count">{count}</span>
              </div>
            ))}
          </div>
          <div className="quick-actions-footer">
            <button className="adm-btn-text" onClick={() => setTab("orders")}>
              View All Orders →
            </button>
          </div>
        </div>
      </section>

      {/* Recent Payouts */}
      <section className="adm-card">
        <div className="card-header-flex" style={{ marginBottom: 12 }}>
          <h2 className="adm-section-title">Recent Payouts</h2>
          <button className="adm-btn-text" onClick={() => setTab("allPayouts")}>
            View All →
          </button>
        </div>
        <div className="adm-table-wrapper">
          <table className="adm-table-light adm-table-compact" style={{ width: "100%" }}>
            <thead>
              <tr>
                <th>Date</th>
                <th style={{ width: "30%" }}>Talent</th>
                <th>Gross</th>
                <th>Net (Talent)</th>
                <th># Orders</th>
                <th>Avg. Value</th>
              </tr>
            </thead>
            <tbody>
              {recentPayouts.map((p) => {
                const dj = p.dj;
                const src = fmtImg(dj?.avatarUrl);
                return (
                  <tr key={p._id} className="hover-row">
                    <td className="muted-text" style={{ fontSize: "0.78rem", whiteSpace: "nowrap" }}>
                      {new Date(p.createdAt).toLocaleDateString(undefined, { month: "numeric", day: "numeric", year: "2-digit" })}
                    </td>
                    <td>
                      {dj ? (
                        <div
                          className="dj-cell dj-cell-compact clickable"
                          onClick={() => navigate(`/admin/dj/${dj._id}`)}
                        >
                          {src ? (
                            <img src={src} className="dj-cell-avatar-sm" alt="" />
                          ) : (
                            <div className="dj-cell-placeholder-sm">{dj.name?.[0]?.toUpperCase() || "?"}</div>
                          )}
                          <span className="dj-link-text" style={{ fontSize: "0.8rem", fontWeight: 600 }}>
                            {dj.name}
                          </span>
                        </div>
                      ) : (
                        <span className="muted-text">Unknown</span>
                      )}
                    </td>
                    <td style={{ fontWeight: 600, fontSize: "0.8rem" }}>${p.totalGross.toFixed(2)}</td>
                    <td style={{ fontWeight: 700, color: "var(--adm-green)", fontSize: "0.8rem" }}>
                      ${p.totalNet.toFixed(2)}
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>
                      <span style={{ fontWeight: 700 }}>{p.ordersCount}</span>{" "}
                      <span style={{ fontSize: "0.78rem" }}>({p.ordersAccepted}/{p.ordersDeclined})</span>
                    </td>
                    <td style={{ fontSize: "0.8rem" }}>${p.avgRequestValue.toFixed(2)}</td>
                  </tr>
                );
              })}
              {recentPayouts.length === 0 && (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 24, color: "#9ca3af" }}>
                    No payouts yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
